import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Home,
  RotateCcw,
  Play,
  Pause,
  Square,
  AlertTriangle,
} from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";

/* ---------- Audio Player with Play / Pause / Stop / Seek ---------- */
function AudioPlayer({ src, lang }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => {
      const d = isFinite(a.duration) ? a.duration : 0;
      setDuration(d);
      setReady(true);
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const play = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      await a.play();
      setPlaying(true);
    } catch (_e) {
      toast.error("Couldn't start audio");
    }
  };
  const pause = () => {
    audioRef.current?.pause();
    setPlaying(false);
  };
  const stop = () => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    setCurrent(0);
    setPlaying(false);
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    const v = Number(e.target.value);
    a.currentTime = v;
    setCurrent(v);
  };

  const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="math-audio-player"
      className="mt-4 bg-white rounded-3xl p-4 border-4 border-white shadow-[0_6px_0_rgba(0,0,0,0.08)]"
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center gap-3">
        {playing ? (
          <button
            data-testid="audio-pause-btn"
            onClick={pause}
            className="w-12 h-12 rounded-full bg-amber-400 hover:bg-amber-500 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5"
            aria-label="Pause"
          >
            <Pause size={22} strokeWidth={3} />
          </button>
        ) : (
          <button
            data-testid="audio-play-btn"
            onClick={play}
            disabled={!ready}
            className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 disabled:text-slate-400 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5"
            aria-label="Play"
          >
            <Play size={22} strokeWidth={3} />
          </button>
        )}
        <button
          data-testid="audio-stop-btn"
          onClick={stop}
          className="w-12 h-12 rounded-full bg-rose-400 hover:bg-rose-500 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5"
          aria-label="Stop"
        >
          <Square size={20} strokeWidth={3} />
        </button>
        <div className="flex-1 min-w-0">
          <input
            data-testid="audio-seek"
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={current}
            onChange={seek}
            disabled={!ready || !duration}
            className="w-full accent-pink-500"
            aria-label="Seek audio"
          />
          <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mt-1 tabular-nums">
            <span data-testid="audio-current">{fmt(current)}</span>
            <span data-testid="audio-duration">{fmt(duration)}</span>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 font-semibold mt-2">
        {t(lang, "audio_hint_label")}
      </p>
    </div>
  );
}

/* ----------------------- Page ------------------------ */
export default function MathResult() {
  const navigate = useNavigate();
  const { lang } = useAuth();
  const [item, setItem] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("dt_math_result");
      if (!raw) {
        navigate("/math", { replace: true });
        return;
      }
      setItem(JSON.parse(raw));
    } catch (_) {
      navigate("/math", { replace: true });
    }
  }, [navigate]);

  const audioSrc = useMemo(() => {
    if (!item?.audio_b64 || !item?.audio_mime) return null;
    return `data:${item.audio_mime};base64,${item.audio_b64}`;
  }, [item]);

  const isHint = item?.type === "math-hint";
  const isSolution = item?.type === "math-solution";
  const captureType = isHint ? "hint" : "solution";

  const noMath =
    (isHint && (item?.text_output || "").trim().toUpperCase().startsWith("NO MATH PROBLEM FOUND")) ||
    (isSolution && /math problem not found/i.test(item?.html || ""));

  const onDownloadSolution = () => {
    if (!item?.html) return;
    try {
      const blob = new Blob([item.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `saral-eye-solution-${Date.now()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Saved!");
    } catch (_) {
      toast.error("Download failed");
    }
  };

  if (!item) return null;

  return (
    <div className="page-shell" data-testid="math-result-page">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/math")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
        >
          <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, isHint ? "math_hint" : "math_solution")}
        </h2>
        <div className="w-12" />
      </div>

      {noMath && (
        <div
          data-testid="math-not-found-banner"
          className="bg-amber-50 border-4 border-amber-200 rounded-3xl p-4 flex items-start gap-3 text-amber-800"
        >
          <AlertTriangle size={22} strokeWidth={2.5} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">{t(lang, "no_math_found_title")}</div>
            <div className="text-sm font-semibold mt-0.5">{t(lang, "no_math_found_desc")}</div>
          </div>
        </div>
      )}

      {!noMath && isHint && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-5 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)]"
        >
          <h3 className="font-heading text-lg font-bold text-slate-700 mb-3">
            {t(lang, "hint_label")}
          </h3>
          <ul data-testid="math-hint-text" className="space-y-2.5">
            {(item.text_output || "")
              .split(/\r?\n/)
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-base text-slate-800 font-medium leading-relaxed"
                >
                  <span
                    aria-hidden
                    className="mt-2 w-2.5 h-2.5 shrink-0 rounded-full bg-gradient-to-br from-pink-400 to-violet-500 ring-2 ring-pink-200 shadow-[0_2px_0_rgba(0,0,0,0.1)]"
                  />
                  <span className="min-w-0">{line}</span>
                </li>
              ))}
          </ul>
        </motion.div>
      )}

      {!noMath && isHint && audioSrc && <AudioPlayer src={audioSrc} lang={lang} />}
      {!noMath && isHint && !audioSrc && (
        <div className="mt-4 text-center text-xs text-slate-500 font-semibold" data-testid="audio-unavailable-msg">
          {item.audio_error ? `Audio: ${item.audio_error}` : t(lang, "audio_unavailable")}
        </div>
      )}

      {!noMath && isSolution && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-2 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)] overflow-hidden"
        >
          <div className="w-full h-[70vh] rounded-2xl overflow-auto bg-[#fdfcf7]">
            <iframe
              data-testid="math-solution-frame"
              title="solution"
              sandbox=""
              srcDoc={item.html}
              scrolling="yes"
              className="block bg-[#fdfcf7] border-0"
              style={{ width: "1100px", height: "1500px" }}
            />
          </div>
          <p className="mt-2 text-center text-[11px] text-slate-500 font-semibold">
            Pinch to zoom · scroll in both directions to read full notebook
          </p>
        </motion.div>
      )}

      {!noMath && isSolution && (
        <button
          data-testid="math-download-btn"
          onClick={onDownloadSolution}
          className="mt-6 w-full bg-emerald-400 hover:bg-emerald-500 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2"
        >
          <Download size={22} strokeWidth={2.5} /> {t(lang, "download")}
        </button>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <button
          data-testid="math-another-btn"
          onClick={() => navigate(`/math/capture/${captureType}`)}
          className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
        >
          <RotateCcw size={16} strokeWidth={2.75} className="shrink-0" />
          <span>{t(lang, "another_problem")}</span>
        </button>
        <button
          data-testid="math-home-btn"
          onClick={() => navigate("/")}
          className="bg-purple-400 hover:bg-purple-500 text-white font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
        >
          <Home size={16} strokeWidth={2.75} className="shrink-0" />
          <span>{t(lang, "home")}</span>
        </button>
      </div>
    </div>
  );
}
